import { videoExtensions } from "config/Constants";

const imageExtensions = ["jpg", "jpeg", "png"];

// --- Default validation rules
const defaultValidationRules = {
  "image":{
    type: "image",
    maxSizeMB: 1,
    allowedTypes: ["image/jpeg", "image/png", "image/jpg"],
    extensions: imageExtensions,
  },
  "video":{
    type: "video",
    maxSizeMB: 10,
    allowedTypes: ["video/mp4", "video/webm"],
    extensions: videoExtensions,
  },
};

const isVideoFile = (file) => {
    const typeOfFile = typeof file;
    if (typeOfFile === "string")
      return videoExtensions?.some((ext) => file?.endsWith(ext));
    if (typeOfFile === "object")
      return (
        file?.type?.startsWith("video/") ||
        videoExtensions?.some((ext) => file?.name?.endsWith(ext))
      );
    return false;
  };
  
  const isImageFile = (file) => {
    const typeOfFile = typeof file;
    if (typeOfFile === "string")
      return imageExtensions?.some((ext) => file?.endsWith(ext));
    if (typeOfFile === "object")
      return (
        file?.type?.startsWith("image/") ||
        imageExtensions?.some((ext) => file?.name?.endsWith(ext))
      );
    return false;
  };

  const getFileType = (file) => {
    return isVideoFile(file) ? "video" : isImageFile(file) ? "image" : null;
  }

// --- Utility to read files as base64
const readAsDataURL = (file) => {
  if (!file) return null;
  if (typeof file === "string") return file;
  if (file?.status === "done" && file?.url) return file?.url;
  
  if (file?.url) return file?.url;

  if (file instanceof File || file instanceof Blob || file?.originFileObj) {
    if(file?.thumbUrl){
      return file?.thumbUrl;
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file?.originFileObj || file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  return null;
};

// --- Helper: validate file dimensions
const validateImageDimensions = (file, rule, t = () => {}) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const valid =
        (!rule?.dimensions?.width || img.width <= rule?.dimensions?.width) &&
        (!rule?.dimensions?.height || img.height <= rule?.dimensions?.height);

      if (!valid) {
        const error = 
          new Error(
            t(`Invalid image dimensions. Expected dimensions`, {
              width: rule?.dimensions?.width,
              height: rule?.dimensions?.height,
              originalWidth: img.width,
              originalHeight: img.height,
            })

          )
          error.dimensions = {
            originalWidth: img.width,
            originalHeight: img.height,
            width: rule?.dimensions?.width,
            height: rule?.dimensions?.height,
          }
          reject(error);
      } else {
        resolve(true);
      }
    };
    img.onerror = () => reject(new Error(t("Unable to read image dimensions.")));
    img.src = URL.createObjectURL(file);
  });
};


// --- Validation handler (pure) -> returns { ok: boolean, message?: string }
const validateFile = async (file, validationRules = {}, t=()=>{}) => {
  const type = getFileType(file);

  if (!type) {
    return { ok: false, message: t("Unsupported file type.") };
  }

  let matchedRule = defaultValidationRules[type];

  if (!matchedRule) return { ok: true };
  matchedRule = {...matchedRule, ...validationRules};
  if(typeof validationRules?.dimensions === "string" && validationRules?.dimensions?.includes("x")){
    matchedRule.dimensions = {
      width: parseInt(validationRules?.dimensions?.split("x")[0]),
      height: parseInt(validationRules?.dimensions?.split("x")[1]),
    };
  }

  // 1) Type check
  if (matchedRule?.allowedTypes && file?.type && !matchedRule?.allowedTypes.includes(file.type)) {
    return {
      ok: false,
      message: t("Only {{types}} are allowed.", { types: matchedRule?.allowedTypes.join(", ") }),
    };
  }

  // 2) Size check
  const maxSizeBytes = (matchedRule?.maxSizeMB || 0) * 1024 * 1024;
  if (typeof file?.size === "number" && maxSizeBytes > 0 && file.size > maxSizeBytes) {
    return {
      ok: false,
      message: t("File must be smaller than {{mb}}MB.", { mb: matchedRule?.maxSizeMB }),
    };
  }

  // 3) Dimension check (for images)
  if (type === "image" && matchedRule?.dimensions) {
    try {
      await validateImageDimensions(file, matchedRule, t);
    } catch (err) {
      console.log({err})
      return { ok: false, message: err?.message || t("Invalid image dimensions."),
        data: err?.dimensions,
       };
    }
  }

  return { ok: true };
};

export {
  validateFile,
  readAsDataURL,
  validateImageDimensions,
  getFileType,
  isVideoFile,
  isImageFile,
  defaultValidationRules,
};
